// apps/web/src/components/amount-input.tsx
import { useEffect, useRef, useState } from 'react'
import { Icon } from './icon'
import { Popover } from './popover'
import { evalExpression } from '../lib/calc'

type Props = {
  value: string
  onChange: (value: string) => void
  decimals?: number
  style?: React.CSSProperties
  className?: string
  disabled?: boolean
  required?: boolean
  placeholder?: string
  id?: string
  'aria-label'?: string
}

// Digits, one separator, optional leading minus — also accepts in-progress
// fragments like "1." or "1," so typing isn't fought mid-keystroke.
const NUMERIC_RE = /^-?\d*[.,]?\d*$/

function roundTo(n: number, decimals: number): number {
  const f = Math.pow(10, decimals)
  return Math.round(n * f) / f
}

function isZeroOrEmpty(v: string): boolean {
  if (v.trim() === '') return true
  const n = Number(v.replace(',', '.'))
  return Number.isFinite(n) && n === 0
}

export function AmountInput({
  value,
  onChange,
  decimals = 2,
  style,
  className,
  disabled,
  required,
  placeholder,
  id,
  'aria-label': ariaLabel,
}: Props) {
  const [focused, setFocused] = useState(false)
  const [display, setDisplay] = useState(value)
  const [padOpen, setPadOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Mirror the external value while the field is idle. While focused we keep the
  // user's in-progress text (e.g. "1." or a cleared zero) untouched.
  useEffect(() => {
    if (!focused) setDisplay(value)
  }, [value, focused])

  const handleFocus = () => {
    setFocused(true)
    if (isZeroOrEmpty(value)) {
      setDisplay('') // local clear — the zero vanishes without marking dirty
    } else {
      inputRef.current?.select()
    }
  }

  const handleChange = (raw: string) => {
    if (raw !== '' && !NUMERIC_RE.test(raw)) return // ignore non-numeric input
    setDisplay(raw)
    onChange(raw.replace(',', '.'))
  }

  // Layout props live on the wrapper; visual props stay on the input.
  const { width, flex, ...inputStyleRest } = style ?? {}

  return (
    <div style={{ position: 'relative', display: 'block', width: width ?? '100%', flex }}>
      <input
        ref={inputRef}
        id={id}
        aria-label={ariaLabel}
        type="text"
        inputMode="decimal"
        className={className}
        style={{ ...inputStyleRest, width: '100%', paddingRight: 30 }}
        value={display}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={() => setFocused(false)}
        onChange={(e) => handleChange(e.target.value)}
      />
      <button
        type="button"
        aria-label="Калькулятор"
        title="Калькулятор"
        disabled={disabled}
        onClick={() => setPadOpen((o) => !o)}
        style={{
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          padding: 0,
          border: 'none',
          background: 'transparent',
          color: 'var(--fg-3)',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <Icon name="calculator" size={15} />
      </button>
      <Popover open={padOpen} onClose={() => setPadOpen(false)} width={200} align="right">
        <CalcPad
          initial={value}
          onCommit={(n) => {
            onChange(String(roundTo(n, decimals)))
            setPadOpen(false)
          }}
        />
      </Popover>
    </div>
  )
}

function CalcPad({
  initial,
  onCommit,
}: {
  initial: string
  onCommit: (n: number) => void
}) {
  const [expr, setExpr] = useState(initial && !isZeroOrEmpty(initial) ? initial : '')
  const [error, setError] = useState(false)

  const append = (s: string) => {
    setError(false)
    setExpr((e) => e + s)
  }
  const equals = () => {
    const n = evalExpression(expr)
    if (n === null) {
      setError(true)
      return
    }
    onCommit(n)
  }

  const keys: { label: string; onClick: () => void }[] = [
    { label: '7', onClick: () => append('7') },
    { label: '8', onClick: () => append('8') },
    { label: '9', onClick: () => append('9') },
    { label: '÷', onClick: () => append('/') },
    { label: '4', onClick: () => append('4') },
    { label: '5', onClick: () => append('5') },
    { label: '6', onClick: () => append('6') },
    { label: '×', onClick: () => append('*') },
    { label: '1', onClick: () => append('1') },
    { label: '2', onClick: () => append('2') },
    { label: '3', onClick: () => append('3') },
    { label: '−', onClick: () => append('-') },
    { label: '0', onClick: () => append('0') },
    { label: '.', onClick: () => append('.') },
    { label: '(', onClick: () => append('(') },
    { label: ')', onClick: () => append(')') },
    { label: 'C', onClick: () => { setError(false); setExpr('') } },
    { label: '⌫', onClick: () => { setError(false); setExpr((e) => e.slice(0, -1)) } },
    { label: '+', onClick: () => append('+') },
    { label: '=', onClick: equals },
  ]

  return (
    <div style={{ padding: 8 }}>
      <input
        readOnly
        value={expr}
        placeholder="0"
        aria-label="Вираз"
        style={{
          width: '100%',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          padding: '6px 8px',
          marginBottom: 8,
          border: `1px solid ${error ? 'var(--danger)' : 'var(--border)'}`,
          borderRadius: 4,
          background: 'var(--bg-1)',
          color: 'var(--fg-1)',
          fontSize: 14,
        }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
        {keys.map((k) => (
          <button
            key={k.label}
            type="button"
            onClick={k.onClick}
            style={{
              padding: '8px 0',
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: k.label === '=' ? 'var(--accent)' : 'var(--bg-2)',
              color: k.label === '=' ? 'var(--accent-fg)' : 'var(--fg-1)',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {k.label}
          </button>
        ))}
      </div>
    </div>
  )
}
